"use strict";

/*
 * Tooltip system rewrite + hardening layer.
 * Public API (window.tooltipApi):
 *  get, open, close, toggle, setup, handlePosition, dismissAll, forceDismissAll, create
 */

(function () {
    // ---------------- Registry ----------------
    const registry = new Map();           // id -> element instance (wow-tooltip)
    const dropdownClass = "tooltip-open";

    // ---------------- Config ----------------
    const guardConfig = {
        enablePointerGuard: true,
        pointerGuardDelayMs: 48,     // dwell outside both areas before auto-close
        scanThrottleRaf: true,
        autoCloseOnGlobalEvents: true,
        autoCloseOnDomDetach: true,
        maxVisibleMs: 3500,             // 0 = disabled (e.g. 8000 to force maximum lifetime)
        closeOnEscape: true
    };

    // Small helper: disable transitions for one frame (used for forced close)
    function withoutTransition(el, action) {
        if (!el) return action?.();
        const previous = el.style.transition;
        const hasInline = el.style.transition !== "";
        el.style.transition = "none";
        try { action?.(); } finally {
            // force reflow so the removal applies instantly
            void el.offsetHeight; // eslint-disable-line no-unused-expressions
            if (hasInline) {
                el.style.transition = previous;
            } else {
                el.style.removeProperty("transition");
            }
        }
        return void(0);
    }

    // Global suppression window to avoid reopen right after forced dismiss
    let suppressUntil = 0;

    function startSuppression(ms = 80) { suppressUntil = performance.now() + ms; }

    function isSuppressed() { return performance.now() < suppressUntil; }

    function dismissAllTooltips(event, options) {
        const force = !event || options?.force;
        if (force) {
            registry.forEach(inst => inst?.close?.(true));
            document.querySelectorAll(`.${dropdownClass}`).forEach(opened => {
                opened.classList.remove(dropdownClass);
                opened.style.visibility = "hidden";
                opened.style.opacity = "0";
            });
            startSuppression();
            return;
        }

        if (event?.stopPropagation) {
            event.stopPropagation();
        }

        let currentId = -1;
        if (event?.target?.attributes) {
            currentId =
                event.target.attributes["data-tooltip-placeholder"]?.value ??
                event.target.attributes["data-tooltip-dropdown"]?.value ??
                -1;
        }

        const dropdowns = document.querySelectorAll("[data-tooltip-dropdown]");
        const currentTooltip = Array
            .from(dropdowns)
            .filter(x => x.attributes["data-tooltip-dropdown"].value === `${currentId}`);

        const mousePos = (typeof event?.clientX === "number" && typeof event?.clientY === "number")
            ? { x: event.clientX, y: event.clientY }
            : { x: -1, y: -1 };
        const boundaries = currentTooltip[0]?.getBoundingClientRect();

        const isInsideCurrentTooltip =
            mousePos.x >= boundaries?.x &&
            mousePos.x <= boundaries?.x + boundaries?.width &&
            mousePos.y >= boundaries?.y &&
            mousePos.y <= boundaries?.y + boundaries?.height;

        if (isInsideCurrentTooltip) {
            event.preventDefault?.();
            return;
        }

        document.querySelectorAll(`.${dropdownClass}`).forEach(opened => {
            if (currentTooltip.includes(opened)) return;
            opened.classList.remove(dropdownClass);
            opened.style.visibility = "hidden";
            opened.style.opacity = "0";
        });
    }

    class tooltip extends HTMLElement {
        Identifier = "";
        placeholder = null;
        dropdown = null;
        _openedAt = 0;

        connectedCallback() {
            const autoId = this.getAttribute("data-tooltip-id") || this.id || this.Identifier;
            if (autoId) {
                this.setup(autoId);
            }
        }

        handleDropdownPosition(event) {
            if (isSuppressed()) return;
            dismissAllTooltips(event);

            if (!this.placeholder) return;

            const placeholderRect = this.placeholder.getBoundingClientRect();
            const placeholderRightX = placeholderRect.x + placeholderRect.width;
            const placeholderTopY = placeholderRect.y;

            if (this.dropdown?.style) {
                this.dropdown.style.top = `${placeholderTopY}px`;
                this.dropdown.style.left = `${placeholderRightX}px`;
                this.dropdown.style.right = "auto";
                const screenPadding = 0;
                this.dropdown.style.transform = `translateX(${-placeholderRect.x + screenPadding}px)`;
            }

            this.open();
        }

        toggle() {
            if (this.dropdown?.classList?.contains(dropdownClass)) {
                this.close();
            } else {
                this.open();
            }
        }

        open() {
            if (isSuppressed()) return;
            if (this.dropdown?.classList?.contains(dropdownClass)) return;
            if (this.dropdown) {
                this.dropdown.style.visibility = "visible";
                this.dropdown.style.opacity = "1";
                this.dropdown.classList.add(dropdownClass);
                this._openedAt = performance.now();
            }
        }

        close(force) {
            if (!this.dropdown) return;
            const action = () => {
                this.dropdown.classList.remove(dropdownClass);
                this.dropdown.style.visibility = "hidden";
                this.dropdown.style.opacity = "0";
            };
            if (force) {
                withoutTransition(this.dropdown, action);
            } else {
                action();
            }
        }

        setup(id) {
            if (!id) return;

            this.Identifier = id;
            registry.set(id, this);

            const placeholders = document.querySelectorAll("[data-tooltip-placeholder]");
            const dropdowns = document.querySelectorAll("[data-tooltip-dropdown]");

            if (placeholders?.length) {
                const filteredP = Array
                    .from(placeholders)
                    .filter(x => x?.attributes["data-tooltip-placeholder"]?.value === `${id}`);
                if (filteredP.length > 0) {
                    this.placeholder = filteredP[0];
                }
            }

            if (dropdowns?.length) {
                const filteredD = Array
                    .from(dropdowns)
                    .filter(x => x?.attributes["data-tooltip-dropdown"]?.value === `${id}`);
                if (filteredD.length > 0) {
                    this.dropdown = filteredD[0];
                    document
                        .querySelector(`#tooltip-demo-content${this.Identifier}`)
                        ?.addEventListener("mouseleave", () => this.toggle());
                }
            }

            this._detachHandlers();

            // Use pointer events for broader device support
            this.placeholder?.addEventListener("pointerenter", this._onMouseEnter = (e) => this.handleDropdownPosition(e));
            this.placeholder?.addEventListener("pointerleave", this._onPlaceholderLeave = (e) => dismissAllTooltips(e));
            this.dropdown?.addEventListener("pointerleave", this._onDropdownLeave = (e) => dismissAllTooltips(e));
        }

        _detachHandlers() {
            if (this.placeholder && this._onMouseEnter) {
                this.placeholder.removeEventListener("pointerenter", this._onMouseEnter);
            }
            if (this.placeholder && this._onPlaceholderLeave) {
                this.placeholder.removeEventListener("pointerleave", this._onPlaceholderLeave);
            }
            if (this.dropdown && this._onDropdownLeave) {
                this.dropdown.removeEventListener("pointerleave", this._onDropdownLeave);
            }
        }

        disconnectedCallback() {
            this._detachHandlers();
            if (this.Identifier && registry.get(this.Identifier) === this) {
                registry.delete(this.Identifier);
            }
        }
    }

    if (!window.customElements.get("wow-tooltip")) {
        window.customElements.define("wow-tooltip", tooltip);
    }

    const api = {
        get: (id) => registry.get(id),
        open: (id) => registry.get(id)?.open(),
        close: (id, force) => registry.get(id)?.close(force),
        toggle: (id) => registry.get(id)?.toggle(),
        setup: (id) => registry.get(id)?.setup(id),
        handlePosition: (id) => {
            const inst = registry.get(id);
            if (!inst) return;
            inst.handleDropdownPosition({});
        },
        dismissAll: (event) => dismissAllTooltips(event),
        forceDismissAll: () => dismissAllTooltips(undefined, { force: true }),
        create: (id) => {
            if (registry.has(id)) return registry.get(id);
            const el = document.createElement("wow-tooltip");
            el.setAttribute("data-tooltip-id", id);
            document.body.appendChild(el);
            requestAnimationFrame(() => {
                if (!registry.get(id)) el.setup(id);
            });
            return el;
        }
    };

    window.tooltipApi = api;

    window.tooltipInterop = window.tooltipInterop || {};

    window.tooltipInterop.initializeTooltip = function (tooltipEle, id) {
        if (tooltipEle && id) {
            tooltipEle.setup(id);
        }
    };

    window.tooltipInterop.dismissAllTooltips = dismissAllTooltips;

    window.TooltipElementClass = tooltip;

    let lastPointer = { x: -9999, y: -9999, t: 0 };
    let scanScheduled = false;

    function pointInRect(p, r) {
        if (!r) return false;
        return p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
    }

    function scheduleScan() {
        if (scanScheduled) return;
        scanScheduled = true;
        const runner = () => {
            scanScheduled = false;
            guardScan();
        };
        guardConfig.scanThrottleRaf ? requestAnimationFrame(runner) : runner();
    }

    function guardScan() {
        const now = performance.now();
        registry.forEach(inst => {
            const dd = inst.dropdown;
            const ph = inst.placeholder;
            if (!dd || !ph) return;
            if (!dd.classList.contains(dropdownClass)) return;

            // Auto lifetime check
            if (guardConfig.maxVisibleMs > 0 && inst._openedAt && now - inst._openedAt > guardConfig.maxVisibleMs) {
                inst.close(true);
                return;
            }

            const dr = dd.getBoundingClientRect();
            const pr = ph.getBoundingClientRect();

            // If element collapsed or detached
            if ((dr.width === 0 && dr.height === 0) || !document.contains(dd) || !document.contains(ph)) {
                inst.close(true);
                return;
            }

            const insideDropdown = pointInRect(lastPointer, dr);
            const insidePlaceholder = pointInRect(lastPointer, pr);

            if (!insideDropdown && !insidePlaceholder) {
                // Dwell outside for pointerGuardDelayMs before closing
                if (now - lastPointer.t >= guardConfig.pointerGuardDelayMs) {
                    inst.close(true);
                }
            }
        });
    }

    if (guardConfig.enablePointerGuard) {
        window.addEventListener("pointermove", e => {
            lastPointer = { x: e.clientX, y: e.clientY, t: performance.now() };
            scheduleScan();
        }, { passive: true, capture: true });
        // Also scan periodically in case no pointermove after layout shifts
        setInterval(() => guardScan(), 500);
    }

    if (guardConfig.autoCloseOnGlobalEvents) {
        const globalClose = () => api.forceDismissAll();
        ["scroll", "resize", "blur"].forEach(ev =>
            window.addEventListener(ev, globalClose, { passive: true }));
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState !== "visible") globalClose();
        }, { passive: true });
        if (guardConfig.closeOnEscape) {
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") globalClose();
            });
        }
    }

    if (guardConfig.autoCloseOnDomDetach) {
        const mo = new MutationObserver(() => {
            // Fast check: close if placeholder/dropdown detached
            registry.forEach(inst => {
                if (!inst.dropdown || !inst.placeholder) return;
                if (!document.contains(inst.dropdown) || !document.contains(inst.placeholder)) {
                    inst.close(true);
                }
            });
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
    }
})();